import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AuthController } from '@firecms/core';
import type { FirebaseApp } from 'firebase/app';
import {
  AdminApiError,
  type AdminOperator,
  loginAdmin,
  logoutAdmin,
} from '../data/admin-api-client';
import {
  clearStoredSession,
  isSessionExpired,
  readStoredSession,
  writeStoredSession,
  type StoredAdminSession,
} from './admin-session-storage';
import {
  signInAdmin,
  signOutAdmin,
  waitForRestoredFirebaseUser,
} from './firebase-admin-session';

export type AdminAuthStatus =
  | 'restoring'
  | 'signed_out'
  | 'authenticating'
  | 'authenticated'
  | 'denied'
  | 'throttled'
  | 'error'
  | 'expired';

export type AdminAuthErrorCode =
  'denied' | 'throttled' | 'network' | 'expired' | null;

export interface AdminAuthState {
  status: AdminAuthStatus;
  session: StoredAdminSession | null;
  errorCode: AdminAuthErrorCode;
}

export interface AdminAuthExtra {
  status: AdminAuthStatus;
  errorCode: AdminAuthErrorCode;
}

export interface TichChiAdminAuthController extends AuthController<
  AdminOperator,
  AdminAuthExtra
> {
  signInWithPassword(username: string, password: string): Promise<void>;
  restoreSession(): Promise<void>;
  invalidateSession(): void;
}

interface AdminAuthControllerOptions {
  getState: () => AdminAuthState;
  setState: (state: AdminAuthState) => void;
  login: (
    username: string,
    password: string,
  ) => Promise<{
    accessToken: string;
    expiresAt: string;
    firebaseToken: string;
    operator: { username: string };
  }>;
  establishFirebaseSession: (customToken: string) => Promise<void>;
  endFirebaseSession: () => Promise<void>;
  hasRestoredFirebaseSession?: () => Promise<boolean>;
  revokeBackendSession?: (accessToken: string) => Promise<void>;
  now?: () => number;
}

export const initialAdminAuthState: AdminAuthState = {
  status: 'signed_out',
  session: null,
  errorCode: null,
};

export function toAdminOperator(session: StoredAdminSession): AdminOperator {
  return {
    uid: session.operator.username,
    displayName: session.operator.username,
    email: null,
    photoURL: null,
    providerId: 'password',
    isAnonymous: false,
  };
}

export function createAdminAuthController({
  getState,
  setState,
  login,
  establishFirebaseSession,
  endFirebaseSession,
  hasRestoredFirebaseSession,
  revokeBackendSession,
  now = Date.now,
}: AdminAuthControllerOptions): TichChiAdminAuthController {
  const teardown = (status: AdminAuthStatus, errorCode: AdminAuthErrorCode) => {
    clearStoredSession();
    void endFirebaseSession();
    setState({ status, session: null, errorCode });
  };

  return {
    get user() {
      const session = getState().session;
      return session ? toAdminOperator(session) : null;
    },
    get initialLoading() {
      // Keeps FireCMS from flashing the sign-in screen while a stored session
      // is being matched against the restored Firebase session.
      return getState().status === 'restoring';
    },
    get authLoading() {
      const status = getState().status;
      return status === 'authenticating' || status === 'restoring';
    },
    // Both stay undefined on purpose. FireCMS renders `authError` as a fatal
    // "Error loading auth" screen with no way back, which is wrong for a
    // recoverable sign-in failure. The login surface owns those messages and
    // reads them from `extra.status`, keeping the operator able to retry.
    authError: undefined,
    authProviderError: undefined,
    get extra() {
      const state = getState();
      return { status: state.status, errorCode: state.errorCode };
    },
    setExtra: () => undefined,
    loginSkipped: false,
    signInWithPassword: async (username, password) => {
      setState({ status: 'authenticating', session: null, errorCode: null });
      try {
        const result = await login(username, password);

        // Order matters: FireCMS renders collections as soon as `user` is set,
        // and the RTDB delegate reads immediately. Establishing the Firebase
        // session first is what prevents a burst of denied reads on sign-in.
        await establishFirebaseSession(result.firebaseToken);

        const session: StoredAdminSession = {
          accessToken: result.accessToken,
          expiresAt: result.expiresAt,
          operator: result.operator,
        };

        if (isSessionExpired(session, now())) {
          teardown('expired', 'expired');
          return;
        }

        writeStoredSession(session);
        setState({ status: 'authenticated', session, errorCode: null });
      } catch (error: unknown) {
        await endFirebaseSession();
        clearStoredSession();

        if (error instanceof AdminApiError && error.status === 429) {
          setState({
            status: 'throttled',
            session: null,
            errorCode: 'throttled',
          });
          return;
        }

        const denied =
          error instanceof AdminApiError &&
          (error.status === 401 || error.status === 403);
        setState({
          status: denied ? 'denied' : 'error',
          session: null,
          errorCode: denied ? 'denied' : 'network',
        });
      }
    },
    /**
     * Re-establishes a session across a page reload. The backend JWT lives in
     * web storage, but the Firebase read credential lives in the SDK's own
     * IndexedDB store — both must be present, or the operator would hold a
     * session token that cannot read anything.
     */
    restoreSession: async () => {
      const stored = readStoredSession();
      if (!stored || isSessionExpired(stored, now())) {
        clearStoredSession();
        setState(initialAdminAuthState);
        return;
      }

      setState({ status: 'restoring', session: null, errorCode: null });

      const firebaseRestored = hasRestoredFirebaseSession
        ? await hasRestoredFirebaseSession()
        : false;

      if (!firebaseRestored) {
        clearStoredSession();
        setState(initialAdminAuthState);
        return;
      }

      setState({ status: 'authenticated', session: stored, errorCode: null });
    },
    getAuthToken: async () => {
      const session = getState().session;
      if (!session || isSessionExpired(session, now())) {
        teardown('expired', 'expired');
        throw new Error('Admin session expired.');
      }
      return session.accessToken;
    },
    invalidateSession: () => teardown('expired', 'expired'),
    signOut: async () => {
      const session = getState().session;
      clearStoredSession();
      await endFirebaseSession();
      if (session && revokeBackendSession) {
        try {
          await revokeBackendSession(session.accessToken);
        } catch {
          // A failed acknowledgement must not keep the operator signed in.
        }
      }
      setState(initialAdminAuthState);
    },
    setUser: (user) => {
      if (!user) {
        teardown('signed_out', null);
      }
    },
  };
}

export function useAdminAuthController({
  firebaseApp,
  apiBaseUrl,
}: {
  firebaseApp?: FirebaseApp;
  apiBaseUrl: string;
}): TichChiAdminAuthController {
  const [state, setReactState] = useState<AdminAuthState>(
    initialAdminAuthState,
  );
  const stateRef = useRef(state);

  const setState = useCallback((nextState: AdminAuthState) => {
    stateRef.current = nextState;
    setReactState(nextState);
  }, []);

  const login = useCallback(
    (username: string, password: string) =>
      loginAdmin(apiBaseUrl, username, password),
    [apiBaseUrl],
  );

  const establishFirebaseSession = useCallback(
    async (customToken: string) => {
      if (!firebaseApp) {
        throw new Error('Firebase is not initialised.');
      }
      await signInAdmin(firebaseApp, customToken);
    },
    [firebaseApp],
  );

  const endFirebaseSession = useCallback(async () => {
    if (!firebaseApp) return;
    try {
      await signOutAdmin(firebaseApp);
    } catch {
      // Already signed out, or Firebase never initialised.
    }
  }, [firebaseApp]);

  const hasRestoredFirebaseSession = useCallback(async () => {
    if (!firebaseApp) return false;
    return waitForRestoredFirebaseUser(firebaseApp);
  }, [firebaseApp]);

  const revokeBackendSession = useCallback(
    (accessToken: string) => logoutAdmin(apiBaseUrl, accessToken),
    [apiBaseUrl],
  );

  const controller = useMemo(
    () =>
      createAdminAuthController({
        getState: () => stateRef.current,
        setState,
        login,
        establishFirebaseSession,
        endFirebaseSession,
        hasRestoredFirebaseSession,
        revokeBackendSession,
      }),
    [
      endFirebaseSession,
      establishFirebaseSession,
      hasRestoredFirebaseSession,
      login,
      revokeBackendSession,
      setState,
    ],
  );

  // Restore once per Firebase app instance. Without this the 7-day session
  // would end at the first page reload.
  useEffect(() => {
    if (!firebaseApp) return;
    void controller.restoreSession();
  }, [controller, firebaseApp]);

  useEffect(() => {
    const session = state.session;
    if (!session) return;
    const delay = Date.parse(session.expiresAt) - Date.now();
    if (delay <= 0) {
      controller.invalidateSession();
      return;
    }
    const timeout = window.setTimeout(
      () => controller.invalidateSession(),
      Math.min(delay, 2_147_483_647),
    );
    return () => window.clearTimeout(timeout);
  }, [controller, state.session]);

  return controller;
}
